import React, { useState } from 'react';

/**
 * ⛔⛔ RESET THE AUTHENTICATOR — THE LOST-PHONE PATH. Card RESET-AUTHENTICATOR,
 * 5 Oct 2026. Daniel, the night of 4 Oct: "I want the authenticator before Friday."
 *
 * The partner pool is MFA-required with TOTP as the only method, so a partner
 * whose phone is lost, replaced or wiped after enrolling has no way back in.
 * Until this control, a lost authenticator was an escalation to Daniel
 * (runbook §3) with no action behind it.
 *
 * ── ⚠⚠ WHY THE CONFIRM STEP IS WORDED THE WAY IT IS ─────────────────────────
 *
 * This hands MORE control to the caller than the password reset beside it does.
 * A reset issues a credential the partner then replaces; THIS lets whoever signs
 * in next BIND THEIR OWN DEVICE as the second factor. So the confirm sentence
 * names the person, says what will happen on their phone, and says plainly that
 * it must only be done once staff are sure who they are speaking to. A generic
 * "Are you sure?" would be a worse control than no confirm step at all, because
 * it trains the click.
 *
 * ⛔ NO SECRET IS EVER SHOWN HERE, AND THERE IS NONE TO SHOW. The QR code is
 * minted by Cognito for the PARTNER during their next sign-in, inside the
 * portal. That is not a gap to close: a staff-visible enrolment secret would let
 * the person who ran the reset enrol their own device, which is the whole attack
 * this confirm step exists to slow down. There is deliberately no result panel
 * with a code in it, and a test asserts no secret-shaped readout exists.
 *
 * ⛔ THE REASON IS STORED VERBATIM in alliance_partner_audit_log.payload and
 * .summary. The helper text tells staff to record WHAT they checked, never the
 * VALUE — the same line the reset carries, for the same reason: without it the
 * obvious thing to type is the identifier you just read off the screen, and the
 * audit log becomes a second copy of what it exists to protect.
 */

/** Must match MIN_REASON_LENGTH in database/partnerAuthenticatorReset.js. */
export const MIN_REASON_LENGTH = 10;

/**
 * ⚠ THE RUNBOOK'S WORDING, NOT THE CODE'S. Every code the server can return is
 * here — a missing one falls through to the raw code, which is exactly the
 * "what do I do now?" moment this mapping removes.
 */
export const ERROR_TEXT = Object.freeze({
  NO_PORTAL_IDENTITY:
    'No portal identity — this partner predates the portal or was never provisioned. '
    + 'They have no authenticator to reset and cannot sign in at all. They need '
    + 'provisioning, not a reset. Escalate to Daniel.',
  COGNITO_USER_NOT_FOUND:
    'Partner not found in the sign-in system. ⛔ Do not tell them it is fixed. Escalate to Daniel.',
  /**
   * ⚠⚠ NOT AN ERROR IN THE USUAL SENSE, AND THE WORDING SAYS SO. There was no
   * registered authenticator to delete — the partner is ALREADY in the state
   * the reset was trying to produce. Telling staff to "retry" would send them
   * round a loop against a condition that cannot change.
   *
   * ⛔⛔ AND IT NOW STATES THAT THE SESSIONS WERE ENDED — P48a. This is the
   * message a RETRY lands on after a first attempt removed the authenticator
   * and then failed to sign the partner out, so it has to confirm the half that
   * just completed. The old wording ("nothing to reset, and nothing is wrong")
   * was true about the authenticator and SILENT about the sessions — which is
   * exactly how the lost handset kept its session while staff were told
   * everything was fine.
   */
  NO_AUTHENTICATOR_ENROLLED:
    'No authenticator was set up. Their sessions have been ended. '
    + 'Ask them to sign in: they will be shown a QR code.',
  /**
   * ⛔⛔ THE HALF-DONE STATE. Something irreversible happened and something
   * necessary did not, so the message does two things at once: stops staff
   * believing it failed, and gets them to run it again IMMEDIATELY — the retry
   * is what ends the session on the handset that is gone. "Escalate" is the
   * SECOND failure's advice, not the first's; making it the first's would turn
   * every one of these into an escalation and the retry that would have fixed
   * it never happens.
   */
  SIGNOUT_FAILED_AFTER_RESET:
    'The authenticator WAS removed but their sessions could not be ended. '
    + 'Run Reset authenticator again now. If it fails twice, escalate to Daniel.',
  COGNITO_UNAVAILABLE:
    'Sign-in system unavailable — AWS did not answer. Wait a minute and retry; if it persists, escalate.',
  POOL_NOT_CONFIGURED:
    'Sign-in system unavailable — the portal sign-in pool is not configured on this server yet. Escalate.',
  USER_NOT_FOUND: 'That partner member no longer exists. Reload the page.',
  REASON_REQUIRED: `A reason is required — at least ${MIN_REASON_LENGTH} characters.`,
});

export function errorText(code) {
  return ERROR_TEXT[code]
    || `The authenticator was not reset (${code || 'unknown error'}). Escalate to Daniel.`;
}

export default function AuthenticatorReset({ member, canReset, invoke, onResetComplete }) {
  const [phase, setPhase] = useState('idle');   // idle | reason | confirm | done
  const [reason, setReason] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // ⛔ THE SERVER IS THE GATE. This only decides whether to OFFER the control.
  if (!canReset) return null;

  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= MIN_REASON_LENGTH;

  function reset() {
    setPhase('idle'); setReason(''); setErr(null);
  }

  async function doReset() {
    if (!reasonOk || busy) return;
    setBusy(true); setErr(null);
    try {
      const call = invoke || ((c, p) => window.zignagenixAPI.invoke(c, p));
      const r = await call('alliance_partner:members:reset-authenticator', {
        partnerUserId: member.id,
        reason: trimmed,
      });
      if (!r || r.error) {
        /**
         * ⚠⚠ THE HALF-DONE CASE KEEPS ITS OWN WORDS. AUDIT_WRITE_FAILED means
         * the authenticator IS gone — telling staff "it did not happen" would
         * send them to retry while the partner is already able to re-enrol, and
         * would bury the one thing Daniel needs to hear.
         */
        setErr(r && r.message ? r.message : errorText(r && r.code));
        setPhase('reason');
        return;
      }
      setPhase('done');
      if (onResetComplete) onResetComplete({ partnerUserId: member.id });
    } catch (e) {
      setErr(errorText(null));
      setPhase('reason');
    } finally {
      setBusy(false);
    }
  }

  const btn = (bg, fg, bd) => ({
    padding: '4px 10px', fontSize: 11, fontWeight: 600, background: bg, color: fg,
    border: `1px solid ${bd}`, borderRadius: 5, cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.5 : 1,
  });

  if (phase === 'idle') {
    return (
      <button
        type="button"
        onClick={() => setPhase('reason')}
        aria-label={`Reset authenticator for ${member.email}`}
        style={btn('#fff', '#0B7285', '#9BD3DD')}
      >
        Reset authenticator
      </button>
    );
  }

  return (
    <div
      data-testid="authenticator-reset-panel"
      style={{ marginTop: 8, padding: 10, background: '#EDFAFC', border: '1px solid #9BD3DD', borderRadius: 6, flex: '1 1 100%' }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0B5563', marginBottom: 6 }}>
        Reset AUTHENTICATOR for {member.email}
      </div>
      <div style={{ fontSize: 11, color: '#0B7285', marginBottom: 8 }}>
        This removes the <strong>authenticator app code</strong> for{' '}
        <strong>partners.zignagenix.net</strong>. Use it when they have lost, replaced or wiped
        their phone. It does <strong>not</strong> reset their password.
      </div>

      {phase !== 'done' && (
        <>
          <label htmlFor="authenticator-reset-reason" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#495057', marginBottom: 4 }}>
            Reason (required)
          </label>
          <textarea
            id="authenticator-reset-reason"
            value={reason}
            disabled={busy}
            onChange={(e) => { setReason(e.target.value); setErr(null); }}
            rows={2}
            placeholder="Partner called 5 Oct, replaced their phone. Called back on the mobile on file to confirm it was them."
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 12, border: '1px solid #CED4DA', borderRadius: 5 }}
          />
          {/* ⛔ THE LINE THAT KEEPS IDENTIFIERS OUT OF THE AUDIT LOG. */}
          <div data-testid="authenticator-reason-helper" style={{ marginTop: 4, fontSize: 11, color: '#868E96' }}>
            Describe <strong>what you checked</strong>, never the value. No codes, no date of
            birth, no patient names — this reason is stored in the audit log.
            Minimum {MIN_REASON_LENGTH} characters.
          </div>

          {err && (
            <div data-testid="authenticator-reset-error" role="alert" style={{ marginTop: 8, padding: 8, background: '#FFF5F5', border: '1px solid #F5C2C7', borderRadius: 5, fontSize: 11, color: '#842029' }}>
              {err}
            </div>
          )}

          {phase === 'confirm' && (
            <div data-testid="authenticator-reset-confirm" style={{ marginTop: 8, padding: 8, background: '#FFF8E1', border: '1px solid #F0B429', borderRadius: 5, fontSize: 11, color: '#8A6100' }}>
              {/* ⚠ THE CARD'S SENTENCE, VERBATIM. It names the person, says what
                  happens on their phone, and states the precondition — because
                  after this, whoever signs in next chooses the second factor. */}
              This removes <strong>{member.email}</strong>&rsquo;s authenticator. At their next
              sign-in they will scan a new QR code with their phone. Only do this after you have
              confirmed it is really them.
            </div>
          )}

          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {phase === 'reason' && (
              <button type="button" disabled={!reasonOk || busy} onClick={() => setPhase('confirm')}
                style={{ ...btn('#0B7285', '#fff', '#0B7285'), opacity: !reasonOk || busy ? 0.5 : 1 }}>
                Continue
              </button>
            )}
            {phase === 'confirm' && (
              <button type="button" data-testid="authenticator-reset-confirm-button" disabled={busy} onClick={doReset}
                style={btn('#0B7285', '#fff', '#0B7285')}>
                {busy ? 'Resetting…' : 'Yes — reset the authenticator'}
              </button>
            )}
            <button type="button" disabled={busy} onClick={reset} style={btn('#fff', '#495057', '#CED4DA')}>
              Cancel
            </button>
          </div>
        </>
      )}

      {phase === 'done' && (
        <div data-testid="authenticator-reset-result">
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0B5563', marginBottom: 6 }}>
            Done. Ask them to sign in again — they&rsquo;ll be shown a new QR code.
          </div>
          {/* ⚠ NOT A CODE, A NEXT STEP. Nothing secret exists on this screen and
              nothing is retrievable later, because nothing was issued. */}
          <div style={{ fontSize: 11, color: '#0B7285' }}>
            Their old authenticator entry no longer works — tell them to delete it from their
            app. They were signed out of every device, including the lost one.
          </div>
          {/* ⛔⛔ THE PAIRING STAFF WILL NEED ON THE SAME CALL. "I lost my phone"
              and "and I can't remember my password" arrive together, and a
              partner who cannot get past the password screen never reaches the
              QR code this reset just unlocked. Said here rather than left for
              them to work out mid-call. */}
          <div data-testid="authenticator-reset-password-hint" style={{ marginTop: 8, padding: 8, background: '#F6F2FF', border: '1px solid #d9c2fb', borderRadius: 5, fontSize: 11, color: '#5B2BB5' }}>
            If they have <strong>also</strong> forgotten their password, use{' '}
            <strong>&ldquo;Reset portal sign-in&rdquo;</strong> as well — both can be done on
            this one call.
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" data-testid="authenticator-reset-done" onClick={reset}
              style={btn('#0B7285', '#fff', '#0B7285')}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
